import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import ReactDOMServer from "react-dom/server";
import {
    AggregateItem,
    TimeSeriesUnits,
} from "~/modules/token_timeseries/types";
import { getRandomColor } from "~/utils/getRandomColor";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";
import { RWA_COIN_ICON } from "~/consts";
import { DEFAULT_TOOLTIP_PROPS } from "~/modules/visualization/consts";
import { TokenAggregatesBarChartTooltip } from "./TokenAggregatesBarChartTooltip";

// // // //

// Width (px) reserved for the category labels on the left of a horizontal bar
// chart. Labels are left-justified within it and truncated with an ellipsis.
const LABEL_WIDTH = 200;

/** Direction of the bars: `bar` is horizontal, `column` is vertical. */
type BarChartOrientation = "bar" | "column";

/** Placement of the legend relative to the plot area. */
type BarChartLegendPosition = "top" | "right" | "bottom" | "left";

/**
 * Maps a legend position to the Highcharts legend alignment/layout options.
 */
const LEGEND_LAYOUT: Record<
    BarChartLegendPosition,
    { align: string; verticalAlign: string; layout: string }
> = {
    top: { align: "center", verticalAlign: "top", layout: "horizontal" },
    bottom: { align: "center", verticalAlign: "bottom", layout: "horizontal" },
    left: { align: "left", verticalAlign: "middle", layout: "vertical" },
    right: { align: "right", verticalAlign: "middle", layout: "vertical" },
};

interface TokenAggregatesBarChartProps {
    data: AggregateItem[];
    /** Horizontal bars or vertical columns. Defaults to horizontal bars. */
    orientation?: BarChartOrientation;
    /** Whether to show the legend. Defaults to hidden. */
    showLegend?: boolean;
    /** Where the legend sits relative to the plot. Defaults to bottom. */
    legendPosition?: BarChartLegendPosition;
    /** The height of the chart. Defaults to 400px. */
    height?: number;
}

export function TokenAggregatesBarChart({
    data,
    orientation = "bar",
    showLegend = false,
    legendPosition = "bottom",
    height = 400,
}: TokenAggregatesBarChartProps) {
    const series = data
        .map((item) => ({
            name: item.group.name,
            value: item.value,
            color: item.group.color || getRandomColor(),
            iconUrl: item.group.icon_url || RWA_COIN_ICON,
        }))
        .sort((a, b) => (a.value > b.value ? -1 : 1));

    // Every item in an aggregate shares the same measure, so the first item's
    // unit applies to the whole chart.
    const unit = data[0]?.measure?.unit;
    const isDollar =
        unit === TimeSeriesUnits.dollar || unit === TimeSeriesUnits.dollars;
    const unitPrefix = isDollar ? "$" : "";
    const measureName = data[0]?.measure?.name ?? "Value";

    const isHorizontal = orientation === "bar";

    const options = {
        title: null,
        chart: {
            backgroundColor: "transparent",
            type: orientation,
            animation: true,
            height,
            // Reserve a fixed gutter for the left-justified category labels
            // on horizontal bars. Columns put their labels under each column,
            // so the margin goes back to auto (just wide enough for the value
            // axis).
            //
            // Every orientation-dependent option below is set explicitly for
            // both orientations: switching type runs `chart.update`, which
            // merges into the previous options, so a key that's merely omitted
            // keeps its value from the other orientation.
            marginLeft: isHorizontal ? LABEL_WIDTH + 15 : undefined,
            spacingLeft: 0,
            // Breathing room above the tallest column (10 is the default).
            spacingTop: isHorizontal ? 10 : 24,
        },
        xAxis: {
            categories: series.map((s) => s.name),
            title: {
                text: null,
            },
            labels: {
                useHTML: true,
                padding: isHorizontal ? 0 : 5,
                align: isHorizontal ? "left" : undefined,
                reserveSpace: isHorizontal ? true : undefined,
                // Keep Highcharts from line-clamping the horizontal labels.
                // Their truncation is the formatter's CSS ellipsis, which needs
                // a fixed width.
                style: isHorizontal
                    ? { textOverflow: "none", whiteSpace: "nowrap" }
                    : { textOverflow: "ellipsis", whiteSpace: undefined },
                formatter: function (
                    this: Highcharts.AxisLabelsFormatterContextObject,
                ): string {
                    // `pos` is the category index, which matches the order of
                    // the `series` array the categories were built from.
                    const item = series[this.pos];
                    if (!item) {
                        return String(this.value);
                    }
                    const iconHTML = `<img src="${item.iconUrl}" alt="${item.name}" style="width: 16px; height: 16px; flex-shrink: 0;" class="rounded-full">`;
                    if (!isHorizontal) {
                        return `<div style="display: flex; align-items: center; gap: 4px; font-size: 12px; color: var(--secondary-foreground);">${iconHTML}<span>${item.name}</span></div>`;
                    }
                    // 16px icon + 4px gap. The text box needs a definite width
                    // or `text-overflow: ellipsis` never engages.
                    return `<div style="display: flex; align-items: center; gap: 4px; width: ${LABEL_WIDTH}px; max-width: ${LABEL_WIDTH}px; overflow: hidden; font-size: 12px; color: var(--secondary-foreground);">${iconHTML}<span style="display: block; min-width: 0; width: ${LABEL_WIDTH - 20}px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${item.name}</span></div>`;
                },
            },
        },
        yAxis: {
            title: {
                text: null,
            },
            labels: {
                formatter: function (
                    this: Highcharts.AxisLabelsFormatterContextObject,
                ) {
                    return `${unitPrefix}${formatCurrencyValueCompact(Number(this.value))}`;
                },
            },
        },
        tooltip: {
            useHTML: true,
            // Render outside the chart's SVG so the wide tooltip isn't clipped
            // https://api.highcharts.com/highcharts/tooltip.outside
            outside: true,
            backgroundColor: "var(--card)",
            style: {
                // The outside container is appended to <body>, so it must sit
                // above the page's sticky/z-indexed UI (e.g. the chart form)
                zIndex: 250,
                color: "var(--foreground)",
            },
            ...DEFAULT_TOOLTIP_PROPS,
            formatter: function (this: any) {
                const point = this.point;
                if (!point) {
                    return "";
                }
                return ReactDOMServer.renderToString(
                    <TokenAggregatesBarChartTooltip
                        name={point.name}
                        iconUrl={point.iconUrl}
                        label={measureName}
                        value={Number(point.y)}
                        unitPrefix={unitPrefix}
                    />,
                );
            },
        },
        plotOptions: {
            // Declared on `series` rather than `bar` so they apply to both
            // orientations - `plotOptions.bar` is ignored by a column chart.
            series: {
                animation: {
                    duration: 1000,
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (this: any) {
                        return `${unitPrefix}${formatCurrencyValueCompact(Number(this.y))}`;
                    },
                },
                borderRadius: 2,
                colorByPoint: true,
                // Every bar is its own group, so the legend lists the points
                // (one entry per bar) rather than the single series. This is
                // what pie series do by default; Highcharts honors it for any
                // series type even though it's absent from the typings.
                legendType: "point",
                showInLegend: showLegend,
            },
        },
        credits: {
            enabled: false,
        },
        series: [
            {
                name: "",
                data: series.map((s) => ({
                    name: s.name,
                    color: s.color,
                    y: s.value,
                    iconUrl: s.iconUrl,
                })),
            },
        ],
        legend: {
            enabled: showLegend,
            ...LEGEND_LAYOUT[legendPosition],
            itemWidth: 150,
            events: {
                // Prevent clicking a legend item from toggling the
                // corresponding bar's visibility in the chart.
                itemClick: function (
                    event: Highcharts.LegendItemClickEventObject,
                ) {
                    event.preventDefault();
                    return false;
                },
            },
        },
    };

    // Flipping orientation inverts the axes, which Highcharts animates as a
    // jarring morph between layouts. Skip the animation for that update only,
    // so data changes still animate.
    const prevOrientation = React.useRef(orientation);
    const orientationChanged = prevOrientation.current !== orientation;
    React.useEffect(() => {
        prevOrientation.current = orientation;
    }, [orientation]);

    return (
        <HighchartsReact
            highcharts={Highcharts}
            options={options}
            updateArgs={[true, true, !orientationChanged]}
        />
    );
}
